import type Phaser from "phaser";
import type { BrickCellKind } from "../data/ChapterData.js";
import type { WorldBrickTextures } from "../data/worldData.js";
import { GOLD_BASE_KEY, type BrickTextureSpec } from "./brickAppearance.js";
import {
  ensureCompositeBrickTexture,
  resolveVisualDisplaySize,
  resolveVisualTextures,
  shouldUseCompositeVisualTexture,
  visualScaleForBrick,
  type BrickVisualTextureSpec,
} from "./brickVisualTextures.js";

export interface BrickSpawnVisualState {
  readonly visualTex: BrickVisualTextureSpec;
  readonly compositeVisualTexture?: string;
  readonly scale: number;
  readonly displayWidth: number;
  readonly displayHeight: number;
  readonly dimGoldVisual: boolean;
  readonly isCircuitVisual: boolean;
}

export function resolveBrickSpawnVisualState(
  scene: Phaser.Scene,
  tex: BrickTextureSpec,
  cfg: {
    readonly kind: BrickCellKind;
    readonly width: number;
    readonly height: number;
    readonly gridX: number;
    readonly gridY: number;
    readonly shatterSalt?: number;
    readonly strength: number;
    readonly worldTextures?: WorldBrickTextures;
  },
): BrickSpawnVisualState {
  const visualTex = resolveVisualTextures(
    scene.textures,
    tex,
    cfg.width,
    cfg.height,
    cfg.gridX,
    cfg.gridY,
    cfg.shatterSalt ?? 0,
    cfg.strength,
    cfg.worldTextures,
  );
  const compositeVisualTexture = shouldUseCompositeVisualTexture(tex, visualTex, cfg.worldTextures)
    ? ensureCompositeBrickTexture(scene, visualTex.base, visualTex.overlay)
    : undefined;
  const visualScale = visualScaleForBrick(tex, cfg.kind, cfg.worldTextures);
  const visualSize = resolveVisualDisplaySize(visualTex, cfg.width, cfg.height, cfg.kind === "mystery" ? undefined : cfg.worldTextures);

  return {
    visualTex,
    compositeVisualTexture,
    scale: visualScale,
    displayWidth: visualSize.width * visualScale,
    displayHeight: visualSize.height * visualScale,
    dimGoldVisual: cfg.kind === "gold" && cfg.worldTextures?.visualFamily === "circuit" && cfg.worldTextures.goldBase === GOLD_BASE_KEY,
    isCircuitVisual: cfg.kind !== "mystery" && cfg.worldTextures?.visualFamily === "circuit",
  };
}
